import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import NameAndPronounsFields from "./NameAndPronounsFields";
import EmailPasswordFields from "./EmailPasswordFields";
import SelectableBoxCommon from "./SelectableBoxCommon";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { ConfirmModal } from "components";
import Confirmation from "./Confirmation";
import { useAuth } from "context/authContext";
import { UserService } from "services/UserService";
import { IUser } from "types";

type SelectedValueType = "nameAndPronouns" | "emailPassword";

const UserAccount = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const [selectedValue, setSelectedValue] =
    useState<SelectedValueType>("nameAndPronouns");
  const [currentStep2, setCurrentStep2] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showIcons, setShowIcons] = useState({
    nameAndPronouns: false,
    emailPassword: false,
  });
  const [namePronounsData, setNamePronounsData] = useState({
    firstName: "",
    lastName: "",
    pronouns: "",
    profileUrl: "",
  });
  const [emailPasswordData, setEmailPasswordData] = useState({
    email: "",
    // password: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch the user's data from your backend or context (this is a mock function)
  const fetchUserData = async () => {
    setLoading(true);
    try {
      const userData = await UserService.getUserByid(currentUser.uid);
      setNamePronounsData({
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        pronouns: userData?.pronouns || "",
        profileUrl: userData?.profileUrl || "",
      });
      setEmailPasswordData({
        email: userData?.email || "",
        // password: "",
      });

      updateShowIcons(userData);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser]);

  // Save changes to Firestore and update currentUser
  const handleSaveChanges = async () => {
    setIsSaving(true);
    setSuccessMessage(null);
    try {
      const updatedUserData = {
        uid: currentUser.uid,
        ...namePronounsData,
        email: emailPasswordData.email,
        // password: emailPasswordData.password,
      };

      if (namePronounsData.profileUrl) {
        await UserService.updateProfileUrl(
          currentUser.uid,
          namePronounsData.profileUrl
        );
      }
      // await UserService.updateUser(currentUser.uid, updatedUserData);
      await UserService.createUser(currentUser.uid, updatedUserData);

      // Update currentUser in AuthContext
      setCurrentUser({
        ...currentUser,
        userInfo: {
          ...currentUser.userInfo,
          ...updatedUserData,
        },
      });

      setSuccessMessage("User details updated successfully!");
      setEmailPasswordData((prevData) => ({
        ...prevData,
        password: "", // Clear password field after saving
      }));
    } catch (error) {
      console.error("Failed to update user:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const updateShowIcons = (data: IUser) => {
    setShowIcons({
      nameAndPronouns: !!(
        data.firstName ||
        data.lastName ||
        data.pronouns ||
        data.profilePicture
      ),
      emailPassword: !!data.email,
    });
  };

  const handleNamePronounsChange = (updatedValues: any) => {
    setNamePronounsData(updatedValues);
  };

  const handleEmailPasswordChange = (updatedValues: any) => {
    setEmailPasswordData(updatedValues);
  };

  const handleOpenModal = () => {
    setConfirmModalOpen(true);
  };
  const handleCloseModal = () => setConfirmModalOpen(false);

  const allSteps: SelectedValueType[] = ["nameAndPronouns", "emailPassword"];

  // Condition to skip the first two steps
  const skipFirstTwoSteps = true;
  const steps = skipFirstTwoSteps ? allSteps.slice(0) : allSteps;

  const headers: Record<SelectedValueType, string> = {
    nameAndPronouns: "Name and Pronoun",
    emailPassword: "Email Address, Password",
  };

  const handleSelectionChange = (value: SelectedValueType) => {
    const stepIndex = allSteps.indexOf(value);
    if (stepIndex !== -1) {
      setCurrentStep2(stepIndex);
      setSelectedValue(value);
    }

    setShowIcons((prevState: any) => ({
      ...prevState,
      agreeToTerms: false,
    }));
  };

  // const handleNextClick2 = () => {
  //   if (currentStep2 < steps.length - 1) {
  //     setCurrentStep2(currentStep2 + 1);
  //     setSelectedValue(steps[currentStep2 + 1]);
  //   }
  // };
  const handleNextClick2 = () => {
    const stepIndex = allSteps.indexOf(selectedValue);

    if (stepIndex !== -1 && stepIndex < allSteps.length - 1) {
      const nextStep = allSteps[stepIndex + 1];
      setSelectedValue(nextStep);
      setCurrentStep2(stepIndex + 1);
    }
  };


  const handleConfirm = () => {
    // Your confirm logic here
    setConfirmModalOpen(false);
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        minHeight="100vh"
        mb={2}
        position={"relative"}
      >
        <Box
          width={{ xs: "100%", md: "22%" }}
          p={2}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              fontSize={18}
              fontWeight={700}
              color={"#595959"}
            >
              User Account
            </Typography>

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="nameAndPronouns"
              onClick={handleSelectionChange}
              text="Name and Pronouns"
              showIcon={showIcons.nameAndPronouns}
            />

            <Typography
              variant="h6"
              gutterBottom
              mt={2}
              fontSize={18}
              fontWeight={700}
              color={"#595959"}
            >
              Security Settings
            </Typography>

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="emailPassword"
              onClick={handleSelectionChange}
              text="Email Address, Password"
              showIcon={showIcons.emailPassword}
            />
            <Box m={2} pt={2}>
              <Button sx={styleSheet.saveChangesBtnMobile} type="submit">
                Save Changes
              </Button>
            </Box>
          </Box>

          <Box
            display={{ xs: "flex", md: "flex" }}
            flexDirection="column"
            mb={2}
            position={"relative"}
          >
            {isCompleted ? (
              <Button sx={styleSheet.completedBtn} type="submit">
                Content Creator Account Pending
              </Button>
            ) : (
              <>

              </>
            )}

            <Box display="flex" flexDirection="column" gap={3} mt={3}>


              <Button
                onClick={handleSaveChanges}
                disabled={isSaving}
                sx={styleSheet.saveChangesBtn}
                type="submit"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
              <Box pt={3} ml={2}>
                <Button
                  onClick={handleOpenModal}
                  sx={styleSheet.buttonDeactive}
                >
                  Deactivate Account
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* //modal deactive // */}
        <ConfirmModal
          title="You’re About to Deactivate Your Account"
          subtitle="Figma ipsum component variant main layer. Line horizontal arrange team editor thumbnail distribute. Follower figma blur connection slice plugin. Text mask hand bullet ipsum slice mask selection bullet list. Polygon align hand bold."
          cancelText="Cancel"
          confirmText="Confirm"
          titleColor="#E10A0A"
          isLoading={false}
          onCancel={handleCloseModal}
          onConfirm={handleConfirm}
          isConfirmModal={isConfirmModalOpen}
          icon={<ErrorOutlineIcon sx={{ color: "#E10A0A", fontSize: 40 }} />}
        />
        <Box
          width={{ xs: "100%", md: "80%" }}
          height={{ xs: "1200px", md: "890px" }}
          position={"relative"}
          bgcolor={"#F8F8F8"}
          sx={{
            borderRadius: "10px",
            border: "1px solid #A9A9A9",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            fontSize={18}
            p={2}
            fontWeight={700}
            color={"#797979"}
          >
            {headers[selectedValue]}
          </Typography>
          <Typography
            sx={{ borderBottom: "2px solid #A9A9A9", width: "100%", mt: 1 }}
          ></Typography>
          {loading ? ( // Corrected from loging to loading
            <Typography>Loading...</Typography>
          ) : (
            <>
              {selectedValue === "nameAndPronouns" && (
                <NameAndPronounsFields
                  onChange={handleNamePronounsChange}
                  initialValues={namePronounsData}
                  showIcons={showIcons}
                  setShowIcons={setShowIcons}
                />
              )}
            </>
          )}

          {selectedValue === "emailPassword" && (
            <EmailPasswordFields
              onChange={handleEmailPasswordChange}
              initialValues={emailPasswordData}
              showIcons={showIcons}
              setShowIcons={setShowIcons}
            />
          )}

          <Box sx={styleSheet.nextBtn7Con}>
            {selectedValue !== "emailPassword" && (
              <Button
                sx={{
                  ...styleSheet.nextBtn2,

                  color: showIcons[selectedValue] ? "white" : "#595959",
                  bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                  "&:hover": {
                    bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                  },
                }}
                onClick={handleNextClick2}
                disabled={!showIcons[selectedValue]}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default UserAccount;

const styleSheet = {
  contentCreatorBtn: {
    position: "absolute",
    top: { xs: -431, md: -222 },
    left: { xs: 234, md: 0 },
    width: { xs: "134px", md: "226px" },
    height: { xs: "39px", md: "62px" },
    fontSize: { xs: "13.64px", md: "18px" },
    fontWeight: 700,
    lineHeight: { xs: "16.37px", md: "21.6px" },
    p: { xs: "5px 0px 5px 5px", md: "8px 20px 8px 20px" },
    backgroundColor: "#233F91",
    textAlign: "start",
    borderRadius: { xs: "7.58px", md: "15px" },
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  completedBtn: {
    position: "absolute",
    top: { xs: -431, md: -222 },
    left: { xs: 234, md: 0 },
    width: { xs: "134px", md: "226px" },
    height: { xs: "39px", md: "62px" },
    fontSize: { xs: "13.64px", md: "18px" },
    fontWeight: 700,
    lineHeight: { xs: "16.37px", md: "21.6px" },
    p: { xs: "5px 0px 5px 5px", md: "8px 20px 8px 20px" },
    backgroundColor: "#F79E1B",
    textAlign: "start",
    borderRadius: { xs: "7.58px", md: "15px" },
    color: "white",
    "&:hover": {
      backgroundColor: "#FFB400",
      color: "white",
    },
  },
  saveChangesBtn: {
    display: { xs: "none", md: "block" },
    width: "214px",
    height: "48px",
    p: "8px, 20px, 8px, 20px",
    fontSize: "15px",
    fontWeight: 700,
    backgroundColor: "#233F91",
    borderRadius: "15px",
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  saveChangesBtnMobile: {
    display: { xs: "block", md: "none" },
    // margingTop: "20px",
    width: "214px",
    height: "48px",
    p: "8px, 20px, 8px, 20px",
    fontSize: "15px",
    fontWeight: 700,
    backgroundColor: "#233F91",
    borderRadius: "15px",
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  textButton: {
    color: "#000000",
    fontWeight: 400,
    fontSize: "17px",
  },
  buttonDeactive: {
    color: "#E10A0A",
    lineHeight: "24px",
    fontWeight: 700,
    fontSize: "15px",
    textAlign: "center",
  },
  nextButton: {
    color: "#595959",
    bgcolor: "#DBDBDB",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 400,
    fontSize: "15px",
  },
  nextBtn7Con: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    py: 2,
    bgcolor: "#F8F8F8",
    display: "flex",
    justifyContent: "center",
  },
  confrimBtn: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    py: 2,
    bgcolor: "#F8F8F8",
    display: "flex",
    justifyContent: "center",
  },
  nextBtn: {
    color: "#FFFFFF",
    bgcolor: "#595959",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 700,
    fontSize: "18px",
    "&:hover": {
      backgroundColor: "#595959",
      color: "#FFFFFF",
    },
  },
  conformBtn: {
    color: "#EDEDED",
    bgcolor: "#233F91",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 700,
    fontSize: "15px",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "#FFFFFF",
    },
  },
  nextBtn2: {
    color: "#595959",
    bgcolor: "#DBDBDB",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 400,
    fontSize: "15px",
  },
  contentCreatorBorder: {
    borderLeft: { xs: "none", md: "9px solid #233F91" },
    height: "60px",
    position: "absolute",
    top: "-222px",
    left: "-15px",
  },

  contentCreatorBorder1: {
    borderLeft: { xs: "none", md: "9px solid #233F91" },
    height: "44px",
    position: "absolute",
    top: "-440px",
    left: "-15px",
  },
};
